import React, { memo, useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Button, Modal, notification, Space, theme } from 'antd'
import { ReloadOutlined, RocketOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import useAppUpdate from 'utils/hooks/useAppUpdate'
import styles from './UpdateAppWrapper.module.scss'

const NOTIFICATION_KEY = 'app-update'

// Konten notifikasi dirender lewat holder statis antd (di luar IntlProvider),
// jadi teks dikirim sudah jadi string; warna diambil dari token theme (ikut dark mode).
// Layout ikon dibuat sendiri (bukan prop `icon` antd) karena kledo.scss meng-override
// `.ant-notification-notice-with-icon` secara global.
const UpdateNotice = ({ title, description, safeHint }) => {
  const { token } = theme.useToken()

  return (
    <div className={styles.content}>
      <span
        className={styles.icon}
        style={{ background: token.colorPrimaryBg, color: token.colorPrimary }}
      >
        <RocketOutlined />
      </span>
      <div className={styles.body}>
        <div className={styles.title} style={{ color: token.colorTextHeading }}>
          {title}
        </div>
        <div className={styles.description} style={{ color: token.colorTextSecondary }}>
          {description}
        </div>
        {safeHint && (
          <div
            className={styles['safe-hint']}
            style={{ background: token.colorSuccessBg, color: token.colorSuccessText }}
          >
            <SafetyCertificateOutlined />
            <span>{safeHint}</span>
          </div>
        )}
      </div>
    </div>
  )
}

const UpdateAppWrapper = () => {
  const { formatMessage } = useIntl()
  const { updateAvailable, isSafeToReload, pathname, onReloadPage } = useAppUpdate()

  const closeNotification = useCallback(() => {
    notification.destroy(NOTIFICATION_KEY)
  }, [])

  // Di halaman input, muat ulang manual bisa menghapus data — minta konfirmasi dulu
  const reloadHandler = useCallback(() => {
    if (isSafeToReload) {
      onReloadPage()
      return
    }

    Modal.confirm({
      title: formatMessage({ id: 'notification.update_confirm_title' }),
      content: formatMessage({ id: 'notification.update_confirm_content' }),
      okText: formatMessage({ id: 'notification.update_confirm_ok' }),
      okButtonProps: { danger: true },
      cancelText: formatMessage({ id: 'button.cancel' }),
      onOk: onReloadPage,
    })
  }, [formatMessage, isSafeToReload, onReloadPage])

  // Tampil lagi setiap pindah halaman selama versi baru belum terpasang
  useEffect(() => {
    if (!updateAvailable) {
      return
    }

    notification.open({
      key: NOTIFICATION_KEY,
      placement: 'bottomRight',
      duration: 0,
      className: styles.notice,
      message: (
        <UpdateNotice
          title={formatMessage({ id: 'notification.update_title' })}
          description={formatMessage({
            id: isSafeToReload
              ? 'notification.update_description'
              : 'notification.update_description_form',
          })}
          safeHint={isSafeToReload ? null : formatMessage({ id: 'notification.update_data_safe' })}
        />
      ),
      actions: (
        <Space size={8}>
          <Button type="text" size="small" onClick={closeNotification}>
            {formatMessage({ id: 'notification.later' })}
          </Button>
          <Button type="primary" size="small" icon={<ReloadOutlined />} onClick={reloadHandler}>
            {formatMessage({ id: 'notification.update_reload' })}
          </Button>
        </Space>
      ),
    })
  }, [updateAvailable, isSafeToReload, pathname, closeNotification, formatMessage, reloadHandler])

  return null
}

export default memo(UpdateAppWrapper)
